import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Inbox as InboxIcon, Building2, Briefcase, ExternalLink, Check, X, Mail } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { OPPORTUNITY_TYPES, PROFESSIONS, localize, formatDateRange } from "../lib/meta";
import { EmptyState } from "./EmptyState";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const STATUS_STYLE = {
  new: "bg-terracotta text-white",
  viewed: "bg-secondary text-slateink/70",
  interested: "bg-[#EBF6F0] text-[#1E6B47]",
  declined: "bg-[#FBEAE7] text-[#B4432B]",
  closed: "bg-[#F5F3EF] text-[#5A544B]",
};

function StatusPill({ status }) {
  const { t } = useLang();
  const s = status || "new";
  return <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${STATUS_STYLE[s] || "bg-secondary"}`} data-testid={`inq-status-${s}`}>{t(`inbox.st_${s}`)}</span>;
}

export function InquiryInbox({ mode }) {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/inquiries").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const patchItem = (r) => setItems((xs) => xs.map((x) => (x.id === r.id ? r : x)));

  const openItem = async (it) => {
    setNote(it.response_note || "");
    setActive(it);
    if (mode === "received" && it.status === "new") {
      try { const r = await api.patch(`/inquiries/${it.id}/view`); setActive(r.data); patchItem(r.data); } catch { /* noop */ }
    }
  };

  const respond = async (response) => {
    setBusy(true);
    try {
      const r = await api.patch(`/inquiries/${active.id}/respond`, { response, note: response === "interested" ? note : undefined });
      patchItem(r.data); setActive(r.data);
      toast.success(response === "interested" ? t("inbox.interested_sent") : t("inbox.st_declined"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };

  const closeInquiry = async () => {
    setBusy(true);
    try { const r = await api.patch(`/inquiries/${active.id}/close`); patchItem(r.data); setActive(r.data); toast.success(t("inbox.closed_toast")); }
    catch { toast.error("Error"); }
    finally { setBusy(false); }
  };

  const typeLabel = (it) => localize(OPPORTUNITY_TYPES, it.opportunity_type, lang) || it.opportunity_type;
  const modalityLabel = (m) => (m === "online" ? t("common.online") : m === "in_person" ? t("common.in_person") : m === "hybrid" ? t("common.hybrid") : null);
  const sentDate = (it) => (it.created_at ? formatDateRange(it.created_at.slice(0, 10), null, lang) : "");

  if (loading) return <p className="text-muted-foreground">{t("common.loading")}</p>;
  if (items.length === 0) return <EmptyState icon={InboxIcon} title={mode === "received" ? t("inbox.empty_prof") : t("inbox.empty_org")} testId="inbox-empty" />;

  return (
    <div className="space-y-3" data-testid="inbox-list">
      {items.map((it) => (
        <button key={it.id} onClick={() => openItem(it)} data-testid={`inbox-item-${it.id}`}
          className="flex w-full flex-col gap-2 rounded-2xl border border-border bg-white p-5 text-left transition-all hover:border-terracotta/40">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              {mode === "received" ? <Building2 className="h-4 w-4 shrink-0 text-terracotta" /> : <Briefcase className="h-4 w-4 shrink-0 text-terracotta" />}
              <p className="truncate font-display font-semibold text-slateink">{mode === "received" ? it.org_name : it.professional_name || "—"}</p>
              {mode === "sent" && it.profession && <span className="rounded-full bg-slateink px-2 py-0.5 text-[11px] font-semibold text-white">{localize(PROFESSIONS, it.profession, lang)}</span>}
            </div>
            <StatusPill status={it.status} />
          </div>
          <p className="text-sm text-muted-foreground">{typeLabel(it)}{it.initiative ? ` · ${it.initiative.name}` : ""}</p>
          <p className="line-clamp-2 text-sm text-slateink/80">{it.description}</p>
          <p className="text-xs text-muted-foreground">{t("inbox.sent_on")}: {sentDate(it)}</p>
          {mode === "sent" && it.status === "interested" && it.response_note && (
            <p className="rounded-lg bg-[#EBF6F0] px-3 py-2 text-xs text-slateink/80"><b>{t("inbox.note")}:</b> {it.response_note}</p>
          )}
        </button>
      ))}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="inbox-detail-dialog">
          {active && (
            <>
              <DialogHeader className="text-left">
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  {mode === "received" ? active.org_name : active.professional_name || "—"}
                  <StatusPill status={active.status} />
                </DialogTitle>
                <DialogDescription>{typeLabel(active)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {active.initiative && (
                  <a href={`/oportunidades/${active.initiative.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-terracotta" data-testid="inbox-opp-link">
                    {t("inbox.view_opportunity")}: {active.initiative.name} <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                <p className="whitespace-pre-line text-slateink/80">{active.description}</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  {active.location && <p><b className="text-slateink/70">{t("inbox.location")}:</b> {active.location}</p>}
                  {modalityLabel(active.modality) && <p><b className="text-slateink/70">{t("inbox.modality")}:</b> {modalityLabel(active.modality)}</p>}
                  {active.timeframe && <p><b className="text-slateink/70">{t("inbox.timeframe")}:</b> {active.timeframe}</p>}
                  {active.language && <p><b className="text-slateink/70">{t("inbox.language")}:</b> {active.language}</p>}
                </div>
                {mode === "received" && (
                  <p className="rounded-lg bg-cream px-3 py-2 text-xs text-slateink/80" data-testid="inbox-contact"><Mail className="mr-1 inline h-3.5 w-3.5" /><b>{t("inbox.contact")}:</b> {active.contact_name} · {active.contact_email}</p>
                )}
                {active.response_note && (
                  <p className="rounded-lg bg-[#EBF6F0] px-3 py-2 text-xs text-slateink/80"><b>{t("inbox.note")}:</b> {active.response_note}</p>
                )}
              </div>

              {mode === "received" && active.status === "closed" && (
                <p className="mt-2 rounded-lg bg-cream px-3 py-2 text-sm text-slateink/80" data-testid="inbox-closed-note">{t("inbox.closed_by_org")}</p>
              )}

              {mode === "received" && active.status !== "closed" && !["interested", "declined"].includes(active.status) && (
                <div className="mt-2 space-y-3 border-t border-border pt-4">
                  <div>
                    <p className="text-sm font-medium text-slateink">{t("inbox.optional_note")}</p>
                    <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("inbox.note_placeholder")} className="mt-1.5" data-testid="inbox-note-input" />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => respond("interested")} disabled={busy} className="flex-1 rounded-full bg-[#1E6B47] hover:bg-[#155235]" data-testid="inbox-interested-btn"><Check className="mr-1.5 h-4 w-4" />{t("inbox.interested")}</Button>
                    <Button onClick={() => respond("declined")} disabled={busy} variant="outline" className="flex-1 rounded-full" data-testid="inbox-decline-btn"><X className="mr-1.5 h-4 w-4" />{t("inbox.decline")}</Button>
                  </div>
                  <p className="text-xs text-muted-foreground">{t("inbox.do_not_share")}</p>
                </div>
              )}
              {mode === "received" && ["interested", "declined"].includes(active.status) && (
                <p className="mt-2 rounded-lg bg-cream px-3 py-2 text-sm text-slateink/80" data-testid="inbox-already-responded">{t("inbox.responded")}: {t(`inbox.st_${active.status}`)}</p>
              )}

              {mode === "sent" && active.status !== "closed" && (
                <div className="mt-2 border-t border-border pt-4">
                  <Button onClick={closeInquiry} disabled={busy} variant="outline" className="rounded-full" data-testid="inbox-close-btn"><X className="mr-1.5 h-4 w-4" />{t("inbox.close_inquiry")}</Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
